import React, { memo, useMemo } from "react";
import moment from "moment";
import { HiCalendar } from "react-icons/hi";
import { IoCutSharp } from "react-icons/io5";
import { DUMMY_REGEX_EXP } from "Constants/constant";
import { validateCurrentDate } from "Utils/utils";
import { toTimeInputValue } from "./advanceModeSplitDefaults";
import ShiftDatePicker from "./ShiftDatePicker";
import { isAdvanceShiftDraft } from "./advanceModeDraftUtils";
import {
  ADVANCE_TIME_INPUT_CLASS,
  AdvanceModeDurationBadge,
  AdvanceModeFieldError,
  AdvanceModeFieldLabel,
  AdvanceModeSection,
  AdvanceModeSectionBody,
  AdvanceModeSectionHeader,
  AdvanceModeTimeRange,
  AdvanceModeTimeRangeSharedError,
} from "./advanceModeUi";
import {
  BASIC_FIELD_REQUIRED_MSG,
  BASIC_SHIFT_ZERO_DURATION_MSG,
  isBasicShiftZeroDuration,
  validateBasicShiftEndTime,
} from "./basicModeValidation";
import "./advanceModeTimePicker.css";

const BasicModeShiftWhen = ({
  shiftDate,
  startTime,
  endTime,
  register,
  setValue,
  getValues,
  watch,
  clearErrors,
  trigger,
  errors,
  selectedShift,
  checkDisabilityOfShiftStatus,
  isRecurringShiftReadOnly = false,
  isShiftDateLocked = false,
  onShiftDateChange = null,
  onAddSplitShift,
  splitSegments = null,
}) => {
  const liveStartTime = watch ? watch("start_time") : startTime;
  const liveEndTime = watch ? watch("end_time") : endTime;

  const zeroDurationError = useMemo(() => {
    if (!liveStartTime || !liveEndTime) return null;
    return isBasicShiftZeroDuration(liveStartTime, liveEndTime)
      ? BASIC_SHIFT_ZERO_DURATION_MSG
      : null;
  }, [liveStartTime, liveEndTime]);

  const startTimeRegisterProps = useMemo(() => {
    const { ref, ...rest } = register("start_time", {
      required: BASIC_FIELD_REQUIRED_MSG,
    });
    return rest;
  }, [register]);

  const endTimeRegisterProps = useMemo(() => {
    const { ref, ...rest } = register("end_time", {
      required: BASIC_FIELD_REQUIRED_MSG,
      validate: (value) =>
        validateBasicShiftEndTime(getValues?.("start_time"), value),
    });
    return rest;
  }, [register, getValues]);

  const datePattern = validateCurrentDate(
    moment(shiftDate ?? null),
    moment(new Date()),
    ["present", "future", "past"],
    true
  )
    ? DUMMY_REGEX_EXP
    : null;

  const formattedShiftDate = shiftDate
    ? moment(shiftDate, ["YYYY-MM-DD", "DD-MM-YYYY"], true).isValid()
      ? moment(shiftDate, ["YYYY-MM-DD", "DD-MM-YYYY"], true).format("YYYY-MM-DD")
      : moment(shiftDate).format("YYYY-MM-DD")
    : "";

  const handleShiftTimeChange = (field, newValue) => {
    setValue(field, newValue, { shouldDirty: true });

    const nextStart =
      field === "start_time" ? newValue : getValues?.("start_time") ?? liveStartTime;
    const nextEnd =
      field === "end_time" ? newValue : getValues?.("end_time") ?? liveEndTime;

    if (nextStart && nextEnd && !isBasicShiftZeroDuration(nextStart, nextEnd)) {
      clearErrors?.(["start_time", "end_time"]);
    } else if (nextStart && nextEnd) {
      trigger?.("end_time");
    }
  };

  const shiftDateError =
    errors?.shift_date?.type === "required"
      ? "This field is required!"
      : errors?.shift_date?.type === "pattern"
      ? validateCurrentDate(
          moment(shiftDate ?? null),
          moment(new Date()),
          ["present", "future", "past"],
          false
        )
      : null;

  const startTimeError =
    errors?.start_time?.message && errors?.start_time?.type !== "validate"
      ? errors.start_time.message
      : errors?.start_time?.type === "required"
      ? BASIC_FIELD_REQUIRED_MSG
      : null;

  const endTimeError =
    !zeroDurationError && errors?.end_time?.message ? errors.end_time.message : null;

  return (
    <AdvanceModeSection className='advance-mode-shift-when'>
      <AdvanceModeSectionHeader
        theme='violet'
        icon={HiCalendar}
        title='Date & Time'
        bordered={false}
        compact
        action={
          <div className='flex flex-wrap items-center gap-1.5'>
            <AdvanceModeDurationBadge
              start={liveStartTime}
              end={liveEndTime}
              tone='violet'
            />
            {onAddSplitShift && (
              <button
                type='button'
                className='advance-mode-split-segment__btn !mt-0 !py-1.5 !px-2.5'
                title='Split Shift'
                onClick={onAddSplitShift}
              >
                <IoCutSharp className='h-3.5 w-3.5' aria-hidden />
                Split
              </button>
            )}
          </div>
        }
      />
      <AdvanceModeSectionBody className='!pt-0' compact>
        <div className='grid min-w-0 grid-cols-1 gap-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] lg:gap-2.5'>
          <div className='advance-mode-panel min-w-0'>
            <AdvanceModeFieldLabel required>Shift date</AdvanceModeFieldLabel>
            <span
              className='mb-1 block text-[10px] font-medium invisible'
              aria-hidden
            >
              Start
            </span>
            <ShiftDatePicker
              setValue={setValue}
              register={register}
              pattern={datePattern}
              value={formattedShiftDate}
              name='shift_date'
              isDisabled={
                isShiftDateLocked ||
                checkDisabilityOfShiftStatus(selectedShift?.status)
              }
              readOnly={isRecurringShiftReadOnly}
              isRequired={true}
              onDateChange={onShiftDateChange}
              ErrorMessage={shiftDateError}
            />
          </div>
          <div className='advance-mode-panel min-w-0'>
            <AdvanceModeFieldLabel required>Shift window</AdvanceModeFieldLabel>
            <AdvanceModeTimeRange
              startField={
                <>
                  <span className='mb-1 block text-[10px] font-medium text-slate-500'>
                    Start
                  </span>
                  <input
                    {...startTimeRegisterProps}
                    type='time'
                    className={ADVANCE_TIME_INPUT_CLASS}
                    onChange={(e) =>
                      handleShiftTimeChange("start_time", e.target.value)
                    }
                    value={toTimeInputValue(liveStartTime)}
                  />
                  <AdvanceModeFieldError
                    message={!zeroDurationError ? startTimeError : null}
                  />
                </>
              }
              endField={
                <>
                  <span className='mb-1 block text-[10px] font-medium text-slate-500'>
                    End
                  </span>
                  <input
                    {...endTimeRegisterProps}
                    type='time'
                    className={ADVANCE_TIME_INPUT_CLASS}
                    onChange={(e) => handleShiftTimeChange("end_time", e.target.value)}
                    value={toTimeInputValue(liveEndTime)}
                  />
                  <AdvanceModeFieldError
                    message={!zeroDurationError ? endTimeError : null}
                  />
                </>
              }
            />
            <AdvanceModeTimeRangeSharedError message={zeroDurationError} />
          </div>
        </div>
        {splitSegments}
      </AdvanceModeSectionBody>
    </AdvanceModeSection>
  );
};

export default memo(BasicModeShiftWhen);
